import { canUseDOM, isObject } from "@utils/general";

export const focusableElements = [
    "a[href]",
    "area[href]",
    "input:not([disabled]):not([type=hidden])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "button:not([disabled])",
    "object",
    "embed",
    "[tabindex]:not(.modal)",
    "audio[controls]",
    "video[controls]",
    '[contenteditable]:not([contenteditable="false"])',
];

const isArrayOrNodeList = (els: any) => {
    if (els === null) {
        return false;
    }
    return Array.isArray(els) || (canUseDOM && typeof els.length === "number");
};

const isReactRefObj = (target: any) => {
    if (target && typeof target === "object") {
        return "current" in target;
    }
    return false;
};

const getTag = (value: any) => {
    if (value == null) {
        return value === undefined ? "[object Undefined]" : "[object Null]";
    }
    return Object.prototype.toString.call(value);
};

const isFunction = (value: any) => {
    if (!isObject(value)) {
        return false;
    }

    const tag = getTag(value);
    return (
        tag === "[object Function]" ||
        tag === "[object AsyncFunction]" ||
        tag === "[object GeneratorFunction]" ||
        tag === "[object Proxy]"
    );
};

export const findDOMElements = (target: any) => {
    if (isReactRefObj(target)) {
        return target.current;
    }
    if (isFunction(target)) {
        return target();
    }
    if (typeof target === "string" && canUseDOM) {
        let selection = document.querySelectorAll(target);
        if (!selection.length) {
            selection = document.querySelectorAll(`#${target}`);
        }
        if (!selection.length) {
            throw new Error(
                `The target '${target}' could not be identified in the dom, tip: check spelling`,
            );
        }
        return selection;
    }
    return target;
};

export const getTarget = (target: any, allElements?: any) => {
    const els = findDOMElements(target);
    if (allElements) {
        if (isArrayOrNodeList(els)) {
            return els;
        }
        if (els === null) {
            return [];
        }
        return [els];
    } else {
        if (isArrayOrNodeList(els)) {
            return els[0];
        }
        return els;
    }
};

export const setScrollbarWidth = (padding: number) => {
    document.body.style.paddingRight = padding > 0 ? `${padding}px` : "";
};

export const getOriginalBodyPadding = () => {
    const style = window.getComputedStyle(document.body, null);

    return parseInt(
        (style && style.getPropertyValue("padding-right")) || "0",
        10,
    );
};

export const getScrollbarWidth = () => {
    let scrollDiv = document.createElement("div");
    // .modal-scrollbar-measure styles // https://github.com/twbs/bootstrap/blob/v4.0.0-alpha.4/scss/_modal.scss#L106-L113
    scrollDiv.style.position = "absolute";
    scrollDiv.style.top = "-9999px";
    scrollDiv.style.width = "50px";
    scrollDiv.style.height = "50px";
    scrollDiv.style.overflow = "scroll";
    document.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    document.body.removeChild(scrollDiv);
    return scrollbarWidth;
};

export const isBodyOverflowing = () => {
    return document.body.clientWidth < window.innerWidth;
};

export const conditionallyUpdateScrollbar = () => {
    const scrollbarWidth = getScrollbarWidth();
    // https://github.com/twbs/bootstrap/blob/v4.0.0-alpha.6/js/src/modal.js#L433
    const fixedContent = document.querySelectorAll<HTMLElement>(
        ".fixed-top, .fixed-bottom, .is-fixed, .sticky-top",
    )[0];
    const bodyPadding = fixedContent
        ? parseInt(fixedContent.style.paddingRight || "0", 10)
        : 0;

    if (isBodyOverflowing()) {
        setScrollbarWidth(bodyPadding + scrollbarWidth);
    }
};
